/** New-OTA dialog: wraps the shared OTAForm for create. On success it shows a
 *  snackbar, invalidates the ['otas'] queries, and navigates to the created OTA's
 *  detail page. */
import { Button } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as otasApi from '@/api/otas';
import type { OTAWrite } from '@/api/otas';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';

import { OTAForm } from './OTAForm';

export function OTACreateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (body: OTAWrite) => otasApi.create(body),
    onSuccess: (ota) => {
      snackbar.show('OTA created.', 'success');
      qc.invalidateQueries({ queryKey: ['otas'] });
      onClose();
      navigate(`/otas/${ota.id}`);
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Create failed.', 'error'),
  });

  return (
    <AppDialog open={open} onClose={onClose} size="md" title="New OTA" disableBackdropClose>
      <OTAForm
        ota={null}
        canManage
        saving={mutation.isPending}
        onSubmit={(body) => mutation.mutate(body)}
        footer={({ submit, saving }) => (
          <>
            {/* AppDialog renders its own action row; keep the form's inline
                actions minimal and aligned right. */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button variant="contained" disabled={saving} onClick={submit}>
                {saving ? 'Creating…' : 'Create OTA'}
              </Button>
            </div>
          </>
        )}
      />
    </AppDialog>
  );
}
